import { useEffect, useRef, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Flex,
  List,
  Icon,
  Text,
  Divider,
  isEqual
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import FieldToggleMenu from "@/appComponents/Blocks/Menus/FieldToggleMenu/FieldToggleMenu";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { appstr } from "@coreapp/utils/appstr";
import { enumObjClassNames, enumObjectTypes } from "@coreapp/constants";
import {
  GetFieldNameLocalizationMap,
  GetPlanAdditionalFieldOptions,
  GetPlanFieldsLocalizationMap,
  GetFieldDefFromName
} from "@/Utils/DataLib";
import { AccordionSections } from "./AccordionList";
import MultiClassFieldToggleMenu, {
  createCompositeFieldId
} from "../../Menus/FieldToggleMenu/MultiClassFieldToggleMenu";
import { BuildFieldOptions as BuildModalFieldOptions } from "./BuildFieldOptions";

export const PlanFieldsModal = ({
  show,
  onClose,
  onChange,
  onSave,
  defaultFields,
  additionalFields,
  isPositionPlan
}) => {
  const [isUpdating, setUpdating] = useState(false);
  const savedAdditionalFields = useRef(null);

  const doc = GetDoc();

  // Capture initial additionalFields when modal opens
  useEffect(() => {
    if (show) {
      savedAdditionalFields.current = additionalFields ? { ...additionalFields } : null;
    } else {
      // Reset when modal closes
      savedAdditionalFields.current = null;
    }
  }, [show]);

  if (!doc || !show) return null;

  const wsMgr = GetWSMgr();

  // Check if additional fields have changed from initial state
  const hasFieldsChanged = () => {
    if (!savedAdditionalFields.current || !additionalFields) {
      return false;
    }

    const savedHolder = savedAdditionalFields.current.holder ?? [];
    const currentHolder = additionalFields.holder ?? [];
    const savedPosition = savedAdditionalFields.current.position ?? [];
    const currentPosition = additionalFields.position ?? [];

    const holderChanged = !isEqual([...savedHolder].sort(), [...currentHolder].sort());
    const positionChanged = !isEqual([...savedPosition].sort(), [...currentPosition].sort());

    return holderChanged || positionChanged;
  };

  const isSaveDisabled = !hasFieldsChanged();

  const holderPlanLocalizationMap = GetFieldNameLocalizationMap(
    GetPlanFieldsLocalizationMap(enumObjClassNames.box, isPositionPlan) ?? {},
    enumObjClassNames.box,
    doc
  );
  const positionPlanLocalizationMap = GetFieldNameLocalizationMap(
    GetPlanFieldsLocalizationMap(enumObjClassNames.position, isPositionPlan) ?? {},
    enumObjClassNames.position,
    doc
  );

  // Additional Fields
  const additionalHolderFieldsOptions = GetPlanAdditionalFieldOptions(
    [],
    defaultFields?.holder ?? [],
    enumObjClassNames.box,
    doc
  );
  const additionalPositionFieldsOptions = isPositionPlan
    ? GetPlanAdditionalFieldOptions(
        [],
        defaultFields?.position ?? [],
        enumObjClassNames.position,
        doc
      )
    : [];

  const additionalFieldDefOptions = isPositionPlan
    ? [
        ...additionalHolderFieldsOptions.map(fieldName =>
          GetFieldDefFromName(fieldName, enumObjClassNames.box)
        ),
        ...additionalPositionFieldsOptions.map(fieldName =>
          GetFieldDefFromName(fieldName, enumObjClassNames.position)
        )
      ].filter(Boolean)
    : [];

  const selectedMultiClassFieldIds = isPositionPlan
    ? Object.entries(additionalFields ?? {}).flatMap(([fieldType, fields]) =>
        (fields ?? []).map(field =>
          createCompositeFieldId(
            field,
            fieldType === "position" ? enumObjClassNames.position : enumObjClassNames.box
          )
        )
      )
    : [];

  /**
   *
   * @param {*} target
   * @param {"holder" | "position"} fieldType
   */
  const ToggleField = (target, fieldType = "holder") => {
    const targetField = target.value;
    const fieldList = additionalFields?.[fieldType] ?? [];
    const targetIdx = fieldList.indexOf(targetField);
    const newFields = [...fieldList];
    if (targetIdx >= 0) {
      newFields.splice(targetIdx, 1);
    } else {
      newFields.push(targetField);
    }
    onChange({
      ...(additionalFields ?? {}),
      [fieldType]: newFields
    });
  };

  const toggleMultiClassField = target => {
    const targetFieldDef = target.fieldDef;
    const fieldName = targetFieldDef.GetFieldName();
    const parentClass = targetFieldDef.GetParentClass();
    if (!parentClass) return;
    const objClassName = parentClass.GetClassName();
    const fieldType = objClassName === enumObjClassNames.position ? "position" : "holder";
    ToggleField({ value: fieldName }, fieldType);
  };

  /**
   *
   * @param {*} field
   * @param {"holder" | "position"} fieldType
   */
  const DeleteField = (field, fieldType = "holder") => {
    const fieldList = additionalFields?.[fieldType] ?? [];
    const newFields = fieldList.filter(f => f !== field);
    onChange({
      ...(additionalFields ?? {}),
      [fieldType]: newFields
    });
  };

  /**
   *
   * @param {*} fieldsList
   * @param {*} hasActions
   * @param {"holder" | "position"} fieldType
   * @param {Record<string, string>} localizationMap
   * @returns
   */
  const BuildFieldOptions = (
    fieldsList,
    hasActions,
    fieldType = "holder",
    localizationMap = {}
  ) => {
    const actionListItemPropsCallback = fieldName => ({
      actions: hasActions
        ? [
            {
              iconName: "trash-can",
              iconProps: { color: "error", size: "xsmall" },
              onClick: () => DeleteField(fieldName, fieldType)
            }
          ]
        : []
    });
    return BuildModalFieldOptions(
      fieldsList,
      isPositionPlan,
      fieldType,
      localizationMap,
      actionListItemPropsCallback
    );
  };

  const SavePlanFields = async () => {
    try {
      setUpdating(true);
      if (onSave) {
        await onSave();
      } else {
        onChange({ ...additionalFields });
      }
      onClose();
    } finally {
      setUpdating(false);
    }
  };

  const fieldTrigger = (
    <Button
      dataTestId="plan-fields-modal-actions-ManageFields"
      modifier="secondary"
      type="button"
      size={"medium"}
      iconProps={{ name: "gear", size: "small" }}
      iconAlignEnd={false}
    >
      {appstr("managefields")}
    </Button>
  );

  const menuListHeader = { label: appstr("Show and hide fields") };

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id={"plan-fields-modal"}
        show={show}
        onClose={onClose}
        minWidth={"medium"}
        maxWidth={"medium"}
        centerScreen={true}
        withShadow={true}
      >
        <DialogTitle divider={true} onClose={onClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"gear"} />
            {appstr("Manage plan fields")}
          </span>
        </DialogTitle>

        <Flex direction={"column"}>
          <DialogContent>
            <Flex direction={"column"} className={"oc-modal-content"} gap={"8px"}>
              <Flex>
                <Text size={"medium"} modifier={"bold"}>
                  {appstr("Basic info")}
                </Text>
              </Flex>
              {isPositionPlan ? (
                <AccordionSections
                  sections={[
                    {
                      name: appstr("Employee fields"),
                      data: defaultFields?.holder,
                      objType: enumObjectTypes.holder,
                      options: BuildFieldOptions(
                        defaultFields?.holder ?? [],
                        false,
                        "holder",
                        holderPlanLocalizationMap
                      ),
                      dataTestId: "plan-fields-basic-info-employee-fields"
                    },
                    {
                      name: appstr("Position fields"),
                      data: defaultFields?.position,
                      objType: enumObjectTypes.position,
                      options: BuildFieldOptions(
                        defaultFields?.position ?? [],
                        false,
                        "position",
                        positionPlanLocalizationMap
                      ),
                      dataTestId: "plan-fields-basic-info-position-fields"
                    }
                  ]}
                />
              ) : (
                <List
                  enableVirtualization={false}
                  options={BuildFieldOptions(
                    defaultFields?.holder ?? [],
                    false,
                    "holder",
                    holderPlanLocalizationMap
                  )}
                  optionSize={"small"}
                  listWidth={"fill"}
                  showCheck={false}
                  disabled={true}
                />
              )}
            </Flex>

            <Divider vertical={false} />

            <Flex direction={"column"} alignItems="flex-start" gap={"8px"}>
              <Flex
                direction={"row"}
                gap={"8px"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Text style={{ textWrap: "nowrap" }} size={"medium"} modifier={"bold"}>
                  {appstr("Additional info")}
                </Text>
                {isPositionPlan ? (
                  <MultiClassFieldToggleMenu
                    dataTestIdPrefix="plan-fields-modal-"
                    useFieldNames
                    selectedFieldIds={selectedMultiClassFieldIds}
                    onChange={toggleMultiClassField}
                    trigger={fieldTrigger}
                    fieldDefs={additionalFieldDefOptions}
                    listProps={{
                      noResultsText: appstr("No results found"),
                      inputProps: {
                        size: "medium"
                      }
                    }}
                    listHeader={menuListHeader}
                  />
                ) : (
                  <FieldToggleMenu
                    dataTestIdPrefix="plan-fields-modal-"
                    trigger={fieldTrigger}
                    selectedFieldNames={additionalFields?.holder ?? []}
                    fieldNames={additionalHolderFieldsOptions}
                    onChange={target => ToggleField(target)}
                    listProps={{
                      noResultsText: appstr("No results found"),
                      inputProps: {
                        size: "medium"
                      }
                    }}
                    stayAlive={true}
                    listHeader={menuListHeader}
                  />
                )}
              </Flex>
              {isPositionPlan ? (
                <AccordionSections
                  sections={[
                    {
                      name: appstr("Employee fields"),
                      data: additionalFields?.holder,
                      objType: enumObjectTypes.holder,
                      options: BuildFieldOptions(
                        additionalFields?.holder ?? [],
                        true,
                        "holder",
                        holderPlanLocalizationMap
                      ),
                      dataTestId: "plan-fields-additional-info-employee-fields"
                    },
                    {
                      name: appstr("Position fields"),
                      data: additionalFields?.position,
                      objType: enumObjectTypes.position,
                      options: BuildFieldOptions(
                        additionalFields?.position ?? [],
                        true,
                        "position",
                        positionPlanLocalizationMap
                      ),
                      dataTestId: "plan-fields-additional-info-position-fields"
                    }
                  ]}
                />
              ) : (
                <List
                  enableVirtualization={false}
                  options={BuildFieldOptions(
                    additionalFields?.holder ?? [],
                    true,
                    "holder",
                    holderPlanLocalizationMap
                  )}
                  optionSize={"small"}
                  listWidth={"fill"}
                  showCheck={false}
                />
              )}
            </Flex>
          </DialogContent>
        </Flex>

        <DialogActions divider={true}>
          <DialogActions.Tertiary dataTestId="plan-fields-modal-actions-Cancel" onClick={onClose}>
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            dataTestId="plan-fields-modal-actions-Save"
            loading={isUpdating}
            disabled={isSaveDisabled}
            onClick={SavePlanFields}
          >
            {appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
